import { useState } from 'react';
import { X, Shield } from 'lucide-react';
import { verifyTOTP } from '../lib/totp';

interface TwoFactorVerifyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (token: string) => void;
  secret: string;
}

export default function TwoFactorVerifyModal({ isOpen, onClose, onVerify, secret }: TwoFactorVerifyModalProps) {
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    try {
      setLoading(true);
      setError('');

      if (!token) {
        setError('Veuillez entrer le code');
        return;
      }

      const isValid = verifyTOTP(token, secret);
      if (!isValid) {
        setError('Code invalide');
        return;
      }

      onVerify(token);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200]">
      <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X size={20} />
        </button>

        <div className="space-y-6">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">
              Vérification en deux étapes
            </h2>
          </div>

          <div className="space-y-4">
            <p className="text-gray-600">
              Veuillez entrer le code à 6 chiffres généré par votre application d'authentification.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code de vérification
              </label>
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                placeholder="000000"
                maxLength={6}
                required
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handleVerify}
                disabled={loading || token.length !== 6}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Vérification...' : 'Vérifier'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}